import React, { useState, useEffect } from 'react';
import { timeAgo } from '../utils/dates';
import { progressLabel } from '../hooks/useAi';

// Re-render once a second while something is running, so the elapsed clock ticks
function useTicker(active) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

// The "AI Assist" strip on the Compare page: summary, flags, analyze-now,
// and a one-question ask box. Renders nothing when the assistant is
// disabled/unavailable — the parent gates on `ai` being non-null.
//
// Props:
//   ai            — data from useEventAi (enabled === true)
//   pendingCount  — number of pending suggestions (parent computes)
//   onAnalyze(directive?) / analyzing / analyzeError
//   onAsk(question) → Promise<{answer,dataAsOf}> / asking
//   onOpenBulkReview() (optional, Phase 4)
function AiEventPanel({ ai, pendingCount, onAnalyze, analyzing, analyzeProgress, analyzeError, onAsk, asking, onOpenBulkReview, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen ?? (pendingCount > 0));
  useTicker(analyzing);
  const progressText = analyzing ? progressLabel(analyzeProgress) : '';
  const [question, setQuestion] = useState('');
  const [qa, setQa] = useState(null); // { question, answer, dataAsOf, error }
  const [showDirective, setShowDirective] = useState(false);
  const [directive, setDirective] = useState('');

  const flagCount = ai.flags?.length || 0;
  const analyzedLabel = ai.analyzedAt ? `analyzed ${timeAgo(ai.analyzedAt)}` : 'not yet analyzed';
  const stale = ai.stale;

  const submitAsk = async (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || asking) return;
    setQa({ question: q, answer: null, error: null });
    try {
      const res = await onAsk(q);
      setQa({ question: q, answer: res.answer, dataAsOf: res.dataAsOf, error: null });
      setQuestion('');
    } catch (err) {
      setQa({ question: q, answer: null, error: err?.response?.data?.error || err.message || 'Failed' });
    }
  };

  const runAnalyze = () => {
    onAnalyze(showDirective && directive.trim() ? directive.trim() : undefined);
  };

  return (
    <div className="compare-sales-section compare-ai-section">
      <div className="sales-header ai-header" onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
        <span className="sales-toggle">{open ? '▼' : '▶'}</span>
        <span className="ai-header-title">AI Assist</span>
        <span className="ai-header-meta">
          {pendingCount > 0 ? `${pendingCount} suggestion${pendingCount === 1 ? '' : 's'}` : 'no pending suggestions'}
          {flagCount > 0 ? ` · ${flagCount} flag${flagCount === 1 ? '' : 's'}` : ''}
          {` · ${analyzedLabel}`}
        </span>
        {stale && <span className="ai-chip ai-chip-stale" title="Market moved or Skybox data is stale since this analysis">STALE</span>}
        {ai.attentionScore != null && ai.attentionScore >= 60 && (
          <span className={`ai-chip ai-chip-priority ${ai.attentionScore >= 80 ? 'p-high' : 'p-med'}`} title="Attention score">
            {ai.attentionScore}
          </span>
        )}
        <span className="ai-header-actions" onClick={e => e.stopPropagation()}>
          {pendingCount > 0 && onOpenBulkReview && (
            <button className="btn-secondary btn-sm" onClick={onOpenBulkReview}>Review all ({pendingCount})</button>
          )}
          <button className="btn-primary btn-sm" onClick={runAnalyze} disabled={analyzing}
            title="Fetch fresh comparables plus TicketMetric sold data (pulled directly) and run the analysis (30 s to 3 min; the page keeps checking until it finishes). Unpriced listings get a first price. Each run costs roughly $0.10–0.50 in model usage; if nothing changed since the last analysis it is skipped for free.">
            {analyzing ? `Analyzing… ${progressText}` : (ai.analyzedAt ? `Re-analyze (${timeAgo(ai.analyzedAt)})` : 'Analyze now')}
          </button>
        </span>
      </div>

      {open && (
        <div className="sales-content ai-content">
          {analyzeError && <div className="ai-inline-error">{analyzeError}</div>}
          {stale && (
            <div className="ai-stale-banner">
              These suggestions were made before the market or your prices changed — treat them as a starting point, or re-analyze.
            </div>
          )}
          {ai.sessionDown && (
            <div className="ai-stale-banner">
              Skybox session is down — the assistant cannot refresh comparables until it is restored, so background analysis is paused.
            </div>
          )}

          {ai.summary ? (
            <div className="ai-summary">{ai.summary}</div>
          ) : (
            <div className="ai-summary ai-summary-empty">
              {analyzing
                ? `Running: ${progressText || 'starting…'} — comparables, TicketMetric sold data (pulled directly), then two model passes.`
                : 'No analysis yet for this event. Click Analyze now to get per-listing suggestions and a market read.'}
            </div>
          )}

          {ai.attentionReasons?.length > 0 && (
            <ul className="ai-reasons">
              {ai.attentionReasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}

          {flagCount > 0 && !ai.summary && (
            <div className="ai-flags-inline">
              {summarizeFlags(ai.flags).map(f => (
                <span key={f.code} className={`ai-flag-chip sev-${f.severity}`} title={FLAG_HELP[f.code] || f.code}>
                  {f.count > 1 ? `${f.count}× ` : ''}{FLAG_LABELS[f.code] || f.code}
                </span>
              ))}
            </div>
          )}

          <div className="ai-directive-row">
            <label className="checkbox-filter">
              <input type="checkbox" checked={showDirective} onChange={e => setShowDirective(e.target.checked)} />
              Give the analyst an instruction
            </label>
            {showDirective && (
              <input
                type="text"
                className="ai-ask-input"
                value={directive}
                onChange={e => setDirective(e.target.value)}
                placeholder='e.g. "be aggressive, we need these gone by Friday" or "hold premium sections"'
                maxLength={500}
              />
            )}
          </div>

          <form className="ai-ask-row" onSubmit={submitAsk}>
            <input
              type="text"
              className="ai-ask-input"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Ask about this event's market… (e.g. why is this slow? what would you do with section 101?)"
              maxLength={1000}
              disabled={asking}
            />
            <button type="submit" className="btn-secondary btn-sm" disabled={asking || !question.trim()}>
              {asking ? 'Thinking…' : 'Ask'}
            </button>
          </form>
          {qa && (
            <div className="ai-answer">
              <div className="ai-answer-q">Q: {qa.question}</div>
              {qa.error
                ? <div className="ai-inline-error">{qa.error}</div>
                : qa.answer
                  ? <div className="ai-answer-a">{qa.answer}</div>
                  : <div className="ai-answer-a ai-summary-empty">Thinking…</div>}
              {qa.dataAsOf && <div className="ai-answer-meta">data as of {timeAgo(qa.dataAsOf)}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const FLAG_LABELS = {
  UNDERCUT: 'above section floor',
  STALE_PRICE: 'stale price',
  BELOW_COST: 'below cost',
  NO_COMPS: 'no comps',
  MARKET_DROP: 'floor dropped 7d',
  MARKET_RISE: 'floor rose 7d',
  LAST_WEEK_UNCOMPETITIVE: 'final week, above median',
  POSSIBLE_UNDERPRICED: 'may be underpriced',
  REPEATED_CUTS_NO_SALES: 'cut repeatedly, no sales',
  RECENTLY_REPRICED: 'just repriced',
  INITIAL_PRICE: 'first price',
};
export const FLAG_HELP = {
  UNDERCUT: 'Our L price sits well above the cheapest competitor in the same section, with the event approaching',
  STALE_PRICE: 'Price has not been touched in a while and the event is approaching',
  BELOW_COST: 'B price is below our average cost per seat',
  NO_COMPS: 'No competitor listings in this section — no market reference',
  MARKET_DROP: 'The section floor fell sharply over the last 7 days',
  MARKET_RISE: 'The section floor rose sharply over the last 7 days',
  LAST_WEEK_UNCOMPETITIVE: 'Inside 7 days and priced above the section median',
  POSSIBLE_UNDERPRICED: 'Our L price is far below the section floor — money may be left on the table',
  REPEATED_CUTS_NO_SALES: 'Price has been cut 3+ times in 14 days and nothing sold at any of those levels — price may not be the lever',
  RECENTLY_REPRICED: 'A pricer changed this price in the last few hours — give it time',
  INITIAL_PRICE: 'A first B price for a listing that had none — from the section comps, our history and TicketMetric sold data (pulled directly)',
};

export function summarizeFlags(flags) {
  const m = new Map();
  for (const f of flags || []) {
    const cur = m.get(f.code) || { code: f.code, count: 0, severity: 0 };
    cur.count++;
    cur.severity = Math.max(cur.severity, f.severity || 0);
    m.set(f.code, cur);
  }
  return [...m.values()].sort((a, b) => b.severity - a.severity || b.count - a.count);
}

export default AiEventPanel;
